import { QuestionOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  chakra,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Link,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup as ChakraRadioGroup,
  Stack,
  Switch,
  Text,
  Tooltip,
  UnorderedList,
  useBoolean,
  useClipboard,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Input, DatePicker } from '@gamma/ui/form-fields';
import { Label } from '@gamma/ui/form-fields';
import AddSharpIcon from '@gamma/ui/icons/AddSharpIcon';
import CheckSharpIcon from '@gamma/ui/icons/CheckSharpIcon';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import VisibilityOffSharpIcon from '@gamma/ui/icons/VisibilityOffSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import { Alert, Modal } from '@gamma/ui/overlay';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  UseFormRegister,
} from 'react-hook-form';
import { useMemo, useState } from 'react';
import moment from 'moment';
import { i18n } from '../../../../shared/localization';
import { TOTAL_API_KEY_DEFINITIONS_LIMIT } from '../../../../shared/constants';
import { useCreateAPIKey } from '../../queries';
import { useApolloClient } from '@apollo/client';
import {
  calculateExpirationDate,
  ExpirationOption,
} from '@investigator/shared/utils';

const { addAPIKeyModal, buttons, expirationValidation, forms } =
  i18n.pages.apiKeys;

interface APIKeyFormValues {
  name: string;
  expirationOption: ExpirationOption;
  customExpirationDate?: Date;
  detectionsRead: boolean;
  detectionsEnabled: boolean;
  alertsRead: boolean;
  alertsEnabled: boolean;
  logsRead: boolean;
  logsEnabled: boolean;
}

const permissionFieldMap = {
  [forms.permissions.detections]: {
    enabled: 'detectionsEnabled',
    read: 'detectionsRead',
  },
  [forms.permissions.alerts]: {
    enabled: 'alertsEnabled',
    read: 'alertsRead',
  },
  [forms.permissions.logs]: {
    enabled: 'logsEnabled',
    read: 'logsRead',
  },
};

interface AddAPIKeyProps {
  isDisabled: boolean;
}

export const AddAPIKey = ({ isDisabled }: AddAPIKeyProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const showToastMessage = useToast();
  const client = useApolloClient();
  const [isAPIKeyHidden, setIsAPIKeyHidden] = useBoolean(true);
  const [storedAPIKey, setStoredAPIKey] = useState<string | undefined>(
    undefined,
  );

  const [createAPIKeyDefinition, { loading, reset }] = useCreateAPIKey({
    onCompleted: (result) => {
      const apiKey = result?.createAPIKeyDefinition?.api_key;
      if (apiKey) {
        setStoredAPIKey(apiKey);
      }
    },
    onError: (error) => {
      let errorMessage = addAPIKeyModal.apiError;
      if (error.message.toLowerCase().includes('already exists')) {
        errorMessage = addAPIKeyModal.alreadyExists;
      }
      showToastMessage({
        status: 'error',
        description: errorMessage,
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });
  const methods = useForm<APIKeyFormValues>({
    defaultValues: {
      name: '',
      expirationOption: ExpirationOption.Days180, // Default to 180 days
      customExpirationDate: undefined,
      detectionsRead: true,
      detectionsEnabled: true,
      alertsRead: true,
      alertsEnabled: true,
      logsRead: true,
      logsEnabled: true,
    },
  });
  const {
    handleSubmit,
    register,
    setValue,
    reset: formReset,
    watch,
    formState: { errors },
  } = methods;
  const isAPIKeyCreated = !!storedAPIKey;

  const { onCopy, hasCopied } = useClipboard(storedAPIKey || '', {
    timeout: 2000,
  });
  const KEY_NAME_MAX_LENGTH = 200;
  const selectedExpirationOption = watch('expirationOption');

  const tomorrowStartOfDay = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const togglePermissions = (key: string, checked: boolean) => {
    const map = permissionFieldMap[key];
    if (!map) return;
    setValue(map.read as keyof APIKeyFormValues, checked, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const resetModalState = () => {
    formReset();
    reset();
    setStoredAPIKey(undefined);
  };

  const handleOpenModal = () => {
    resetModalState();
    onOpen();
  };

  const handleCloseModal = () => {
    if (storedAPIKey) {
      client.cache.evict({ fieldName: 'listAPIKeyDefinitions' });
      client.cache.gc();
    }
    resetModalState();
    onClose();
  };

  const handleCreateAPIKey = handleSubmit(async (data) => {
    const { name, expirationOption, customExpirationDate } = data;

    // Update form to show the actual values being submitted (in case DatePicker has "last good value")
    setValue('name', name);
    setValue('expirationOption', expirationOption);
    if (expirationOption === ExpirationOption.Custom) {
      setValue('customExpirationDate', customExpirationDate);
    }

    const expirationDate = calculateExpirationDate(
      expirationOption,
      customExpirationDate,
    );

    await createAPIKeyDefinition({
      variables: {
        name,
        end_ts: expirationDate ? moment(expirationDate).unix() : null,
      },
    });
    formReset();
  });

  return (
    <>
      <Tooltip
        label={i18n.formatString(
          addAPIKeyModal.disabledTooltip,
          TOTAL_API_KEY_DEFINITIONS_LIMIT,
        )}
        placement="left"
        isDisabled={!isDisabled}
      >
        <Button
          variant={'solid'}
          isDisabled={isDisabled}
          colorScheme="gray"
          leftIcon={<AddSharpIcon h={'18px'} w={'18px'} />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenModal();
          }}
        >
          {buttons.newKey}
        </Button>
      </Tooltip>
      <Modal
        hideDivider
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={handleCloseModal}
        closeOnEsc={!loading && !isAPIKeyCreated}
        closeOnOverlayClick={!loading && !isAPIKeyCreated}
        isCloseDisabled={loading}
        isCentered
        size="lg"
        title={
          isAPIKeyCreated ? addAPIKeyModal.created.title : addAPIKeyModal.title
        }
        body={
          isAPIKeyCreated ? (
            <VStack alignItems="left" gap={4}>
              <Alert
                status="warning"
                variant={'left-accent'}
                title={addAPIKeyModal.created.alertTitle}
              >
                {addAPIKeyModal.created.alertBody}
              </Alert>
              <VStack w="full" gap={0.5}>
                <HStack align={'center'} gap={2} w="full">
                  <FormControl>
                    <FormLabel
                      display={'inline-flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      gap={2}
                    >
                      {addAPIKeyModal.created.inputTitle}
                      <Popover trigger="hover" placement="right-end">
                        <PopoverTrigger>
                          <QuestionOutlineIcon />
                        </PopoverTrigger>
                        <PopoverContent maxW="300px" w="auto" h={'auto'}>
                          <PopoverBody borderRadius="5px">
                            <Text
                              align={'left'}
                              fontWeight={'normal'}
                              fontSize={'smaller'}
                            >
                              {addAPIKeyModal.created.infoText}
                            </Text>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    </FormLabel>
                    <Input
                      label=""
                      value={storedAPIKey || ''}
                      type={isAPIKeyHidden ? 'password' : 'text'}
                      isReadOnly
                      name="api-key"
                      rightElement={
                        <IconButton
                          onClick={setIsAPIKeyHidden.toggle}
                          aria-label="toggle-api-key"
                          icon={
                            isAPIKeyHidden ? (
                              <VisibilitySharpIcon w="18px" h="18px" />
                            ) : (
                              <VisibilityOffSharpIcon w="18px" h="18px" />
                            )
                          }
                        />
                      }
                    />
                  </FormControl>
                  <IconButton
                    onClick={onCopy}
                    aria-label="copy-api-key"
                    alignSelf={'flex-end'}
                    icon={<ContentCopySharpIcon w="18px" h="18px" />}
                  />
                </HStack>
                {hasCopied && (
                  <Text
                    alignSelf={'start'}
                    fontSize="sm"
                    display={'inline-flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    gap={0.5}
                  >
                    <Center
                      width={'min-content'}
                      height={'min-content'}
                      borderRadius={'100%'}
                      p={0.25}
                      bg={'green'}
                    >
                      <CheckSharpIcon
                        color={'white'}
                        w="15px"
                        h="15px"
                        transform={'auto'}
                        scaleX={0.8}
                        scaleY={0.8}
                      />
                    </Center>
                    &nbsp;&nbsp;
                    {addAPIKeyModal.created.inputCopiedMessage}
                  </Text>
                )}
              </VStack>
              <VStack align={'left'}>
                <Text aria-required mb={-1}>
                  {addAPIKeyModal.created.permissions}
                </Text>
                <Box>
                  <Text fontWeight={'semibold'}>
                    {addAPIKeyModal.created.detections}
                  </Text>
                  <UnorderedList fontWeight={'light'}>
                    <ListItem>{addAPIKeyModal.created.readAccess}</ListItem>
                  </UnorderedList>
                </Box>
                <Box>
                  <Text fontWeight={'semibold'}>
                    {addAPIKeyModal.created.alerts}
                  </Text>
                  <UnorderedList fontWeight={'light'}>
                    <ListItem>{addAPIKeyModal.created.readAccess}</ListItem>
                  </UnorderedList>
                </Box>
                <Box>
                  <Text fontWeight={'semibold'}>
                    {addAPIKeyModal.created.logs}{' '}
                  </Text>
                  <UnorderedList fontWeight={'light'}>
                    <ListItem>{addAPIKeyModal.created.readAccess}</ListItem>
                  </UnorderedList>
                </Box>
              </VStack>
            </VStack>
          ) : (
            <FormProvider {...methods}>
              <VStack alignItems="left" gap={4} maxW="100%">
                <Text>
                  {addAPIKeyModal.description}
                  <br />
                  <Link
                    isExternal
                    color="text.link"
                    textDecoration="none"
                    href="../docs/api.html"
                  >
                    {addAPIKeyModal.learnMore}
                  </Link>
                </Text>
                <Input
                  isDisabled={loading}
                  autoFocus
                  isRequired
                  label={forms.name.label}
                  placeholder={forms.name.placeholder}
                  type="text"
                  data-testid="api-key-name-input"
                  error={errors.name?.message}
                  {...register('name', {
                    required: forms.name.required,
                    maxLength: {
                      value: KEY_NAME_MAX_LENGTH,
                      message: addAPIKeyModal.keyLengthError,
                    },
                    validate: (value) => {
                      if (!value.trim()) {
                        return forms.name.whitespaceError;
                      }
                      return true;
                    },
                  })}
                />
                <Box
                  w="100%"
                  maxW="100%"
                  p={1}
                  data-testid="expiration-section"
                >
                  <Controller
                    name="expirationOption"
                    control={methods.control}
                    rules={{
                      required: expirationValidation.expirationOptionRequired,
                    }}
                    render={({ field }) => (
                      <Label
                        name="expirationOption"
                        label={forms.expiration.newKeyExpirationLabel}
                        isRequired
                        data-testid="expiration-option-radio"
                      >
                        <ChakraRadioGroup
                          name="expirationOption"
                          id="expirationOption"
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <Stack direction="column" gap={0.5}>
                            <Radio
                              value={ExpirationOption.Days180}
                              isDisabled={loading}
                            >
                              {forms.expiration.options['180days']}
                            </Radio>
                            <Radio
                              value={ExpirationOption.OneYear}
                              isDisabled={loading}
                            >
                              {forms.expiration.options['1year']}
                            </Radio>
                            <Radio
                              value={ExpirationOption.Custom}
                              isDisabled={loading}
                            >
                              {forms.expiration.options.custom}
                            </Radio>
                            {selectedExpirationOption ===
                              ExpirationOption.Custom && (
                              <Box
                                w="100%"
                                maxW="100%"
                                pl={6}
                                mt={1}
                                mb={1}
                                data-testid="custom-date-picker-container"
                                sx={{
                                  '& > *': {
                                    width: '100%',
                                  },
                                  '& button': {
                                    width: '100%',
                                    minWidth: '100%',
                                  },
                                }}
                              >
                                <Controller
                                  name="customExpirationDate"
                                  control={methods.control}
                                  rules={{
                                    required:
                                      selectedExpirationOption ===
                                      ExpirationOption.Custom
                                        ? expirationValidation.customDateRequired
                                        : false,
                                    validate: (value) => {
                                      if (
                                        selectedExpirationOption ===
                                          ExpirationOption.Custom &&
                                        value
                                      ) {
                                        const selectedDate =
                                          moment(value).startOf('day');
                                        const minDate =
                                          moment(tomorrowStartOfDay).startOf(
                                            'day',
                                          );
                                        if (selectedDate.isBefore(minDate)) {
                                          return expirationValidation.dateMustBeTomorrow;
                                        }
                                      }
                                      return true;
                                    },
                                  }}
                                  render={({
                                    field: dateField,
                                    fieldState,
                                  }) => (
                                    <DatePicker
                                      name="customExpirationDate"
                                      isDisabled={loading}
                                      label=""
                                      isLabelHidden={true}
                                      selectedDate={dateField.value}
                                      onChange={dateField.onChange}
                                      minDate={tomorrowStartOfDay}
                                      error={fieldState.error?.message}
                                      data-testid="custom-expiration-date-picker"
                                      width="100%"
                                    />
                                  )}
                                />
                              </Box>
                            )}
                            <Radio
                              value={ExpirationOption.NoExpiration}
                              isDisabled={loading}
                            >
                              {forms.expiration.options.noExpiration}
                            </Radio>
                          </Stack>
                        </ChakraRadioGroup>
                      </Label>
                    )}
                  />
                </Box>
                <FormControl isRequired>
                  <FormLabel mb={1}>{forms.permissions.label}</FormLabel>
                  <VStack
                    alignItems={'left'}
                    border={`1px solid`}
                    borderColor={'whiteAlpha.400'}
                    gap={3}
                    px={3.5}
                    py={3}
                    borderRadius={4}
                  >
                    <PermissionForm
                      title={forms.permissions.detections}
                      withBorderBottom
                      register={register}
                      togglePermissions={togglePermissions}
                      popoverDescriptionText={addAPIKeyModal.detectionsPopover}
                      writeAccess
                    />
                    <PermissionForm
                      title={forms.permissions.alerts}
                      withBorderBottom
                      register={register}
                      togglePermissions={togglePermissions}
                      popoverDescriptionText={addAPIKeyModal.alertsPopover}
                      writeAccess
                    />
                    <PermissionForm
                      title={forms.permissions.logs}
                      register={register}
                      togglePermissions={togglePermissions}
                      writeAccess={false}
                    />
                  </VStack>
                </FormControl>
              </VStack>
            </FormProvider>
          )
        }
        footer={
          isAPIKeyCreated ? (
            <Button
              variant="solid"
              colorScheme="gray"
              onClick={handleCloseModal}
              isDisabled={loading}
            >
              {addAPIKeyModal.created.buttons.close}
            </Button>
          ) : (
            <HStack>
              <ButtonGroup>
                <Button
                  variant="solid"
                  colorScheme="gray"
                  onClick={handleCloseModal}
                  isDisabled={loading}
                >
                  {addAPIKeyModal.buttons.cancel}
                </Button>
                <Button
                  variant="solid"
                  colorScheme="blue"
                  isDisabled={loading}
                  isLoading={loading}
                  onClick={handleCreateAPIKey}
                  data-testid="create-api-key-button"
                >
                  {addAPIKeyModal.buttons.createKey}
                </Button>
              </ButtonGroup>
            </HStack>
          )
        }
      />
    </>
  );
};

interface PermissionFormProps {
  title: string;
  withBorderBottom?: boolean;
  isComingSoon?: boolean;
  register?: UseFormRegister<APIKeyFormValues>;
  togglePermissions?: (key: string, checked: boolean) => void;
  popoverDescriptionText?: string;
  writeAccess: boolean;
}

const PermissionForm = ({
  title,
  withBorderBottom,
  isComingSoon,
  togglePermissions,
  popoverDescriptionText,
  writeAccess,
}: PermissionFormProps) => {
  const map = permissionFieldMap[title];
  const { control, setValue } = useFormContext<APIKeyFormValues>();

  if (!map) return null;

  return (
    <Box
      pb={3}
      mb={!isComingSoon ? 2 : undefined}
      borderBottom={withBorderBottom ? '1px solid' : undefined}
      borderBottomColor={withBorderBottom ? 'whiteAlpha.400' : undefined}
      w={'full'}
    >
      <Flex justify={'space-between'} w="full" align={'start'}>
        <Box
          as="span"
          textColor={isComingSoon ? 'GrayText' : undefined}
          fontWeight={'normal'}
          display={'inline-flex'}
          justifyContent={'center'}
          alignItems={'center'}
          gap={2}
        >
          {title}
          {isComingSoon && ` ${forms.permissions.comingSoon}`}
          {!isComingSoon && popoverDescriptionText && (
            <Popover trigger="hover" placement="right-end">
              <PopoverTrigger>
                <QuestionOutlineIcon />
              </PopoverTrigger>
              <PopoverContent maxW="300px" w="auto" h={'auto'}>
                <PopoverBody borderRadius="5px">
                  <Text
                    align={'left'}
                    fontWeight={'normal'}
                    fontSize={'smaller'}
                  >
                    {popoverDescriptionText}
                  </Text>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </Box>
        <Controller
          name={map.enabled as keyof APIKeyFormValues}
          control={control}
          render={({ field }) => {
            const switchChecked = isComingSoon ? false : !!field.value;
            return (
              <Switch
                isDisabled
                isReadOnly
                size="xs"
                isChecked={switchChecked}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  setValue(
                    map.read as keyof APIKeyFormValues,
                    e.target.checked,
                    {
                      shouldValidate: true,
                      shouldDirty: true,
                    },
                  );
                  togglePermissions &&
                    togglePermissions(title, e.target.checked);
                }}
                disabled
              />
            );
          }}
        />
      </Flex>
      {!isComingSoon && (
        <VStack mt={1.5} gap={1} alignItems={'start'} w="full">
          <Controller
            name={map.read as keyof APIKeyFormValues}
            control={control}
            render={({ field }) => (
              <Checkbox
                isDisabled
                isReadOnly
                isChecked={!!field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  setValue(
                    map.enabled as keyof APIKeyFormValues,
                    e.target.checked,
                    {
                      shouldValidate: true,
                      shouldDirty: true,
                    },
                  );
                }}
              >
                {forms.permissions.read.title}{' '}
                <chakra.span fontSize={'smaller'} color="text.secondary">
                  {i18n.formatString(
                    forms.permissions.read.body,
                    title.toLowerCase(),
                  )}
                </chakra.span>
              </Checkbox>
            )}
          />
          {writeAccess && (
            <Checkbox disabled>
              {forms.permissions.write.title}{' '}
              <chakra.span fontSize={'smaller'}>
                {forms.permissions.write.body}
              </chakra.span>
            </Checkbox>
          )}
        </VStack>
      )}
    </Box>
  );
};
